import { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody } from '@heroui/react';
import { toast } from 'sonner';
import { useParams } from 'react-router';
import { useEvaluacion } from '@/hooks';
import { ErrorMessage, Loading, SurveyLayout } from '@/components';
import useStore from '@/store/main/action';
import { API_URL, ESTATUS_EVALUACION } from '@/constants';
import { formatPonderacion } from '@/utils';
import { simpleFetch } from '@/utils/simpleFetch';
import {
    agruparSecciones,
    calcularPonderacionSeccion,
    construirPayloadGuardado,
    contarObligatoriosPendientes,
    contarRespondiblesRespondidos,
    hayRespuestas,
    mapearRespuestasGuardadas,
    mostrarHeaderSeccion,
    PonderacionSeccion,
    ReactivoConOpciones,
    RespuestaReactivo,
} from './helpers';
import ReactivoRow from './ReactivoRow';

// Flujo nTipo 2 (nuevo) — separado del flujo viejo (views/survey). Renderiza la
// evaluación en una sola página agrupada por secciones: cada reactivo de tipo 1
// muestra su texto, un RadioGroup con las opciones (y su ponderación en %), y un
// textarea de observaciones cuando bMostrarObservaciones lo pide. Las respuestas
// viven en estado local (key `${nSeccion}-${nReactivo}`). Cada sección muestra
// junto a su título la ponderación obtenida vs el total (ej. "15% de 20%"),
// calculada en vivo. Al pie, un footer sticky con Guardar (requiere ≥1
// contestación) y Enviar (requiere todos los obligatorios respondidos); al
// enviar se muestra la pantalla final con cMensajeFinal. Ambos botones hacen
// POST a /Guarda/Evaluacion con el mismo payload (shape de encuestas):
// Guardar manda bFinalizado=false, Enviar true. La ruta aún no existe en el
// backend; por ahora solo se formatea la petición.
const EvaluacionView = () => {
    const { evaluacion, loading, notFound } = useEvaluacion();
    const { cToken } = useParams();
    const { primaryColor, setPrimaryColor } = useStore();
    const [respuestas, setRespuestas] = useState<Record<string, RespuestaReactivo>>({});
    const [guardando, setGuardando] = useState(false);
    const [enviado, setEnviado] = useState(false);

    // Aplica el color primario de la evaluación (SurveyLayout lo usa en los bordes).
    // El guard evita dispatch en cada render (setPrimaryColor no es estable).
    useEffect(() => {
        const color = evaluacion?.cColorPrimario ? `#${evaluacion.cColorPrimario}` : null;
        if (color && color !== primaryColor) {
            setPrimaryColor(color);
        }
    }, [evaluacion?.cColorPrimario, primaryColor, setPrimaryColor]);

    // Al obtener la evaluación (fetch inicial o refetch), restaura las
    // respuestas previamente guardadas para el token: cada opción guardada
    // queda seleccionada en su RadioGroup y la observación prellena su
    // textarea. Corre cuando cambia `evaluacion`; no pisa ediciones del
    // usuario porque el estado no vuelve a cambiar mientras contesta.
    useEffect(() => {
        if (evaluacion) {
            setRespuestas(mapearRespuestasGuardadas(evaluacion));
        }
    }, [evaluacion]);

    const secciones = useMemo(
        () => (evaluacion ? agruparSecciones(evaluacion) : []),
        [evaluacion],
    );

    // Lista plana de TODOS los reactivos de la evaluación. Se memoiza una vez
    // por cambio de `secciones` (no por cada `respuestas`) y se reusa para
    // alimentar los contadores memoizados — antes se recalculaba con
    // `secciones.flatMap(...)` en cada uno, dos veces por recompute.
    const reactivosPlanos = useMemo(
        () => {
            const out: ReactivoConOpciones[] = [];
            for (const seccion of secciones) {
                for (const r of seccion.reactivosFlat) out.push(r);
            }
            return out;
        },
        [secciones],
    );

    // Ponderación por sección precalculada UNA vez por cambio de
    // `secciones`/`respuestas`. Antes se calculaba dentro del .map() del
    // render, ejecutándose en cada keystroke del usuario sobre cualquier
    // textarea. Ahora se reusa `calcularPonderacionGlobal` (que internamente
    // ya itera secciones) y se indexa por nSeccion en un Map.
    const ponderacionesPorSeccion = useMemo(() => {
        const map = new Map<number, PonderacionSeccion>();
        for (const { seccion, reactivos } of secciones) {
            map.set(seccion.nSeccion, calcularPonderacionSeccion(reactivos, respuestas));
        }
        return map;
    }, [secciones, respuestas]);

    // Footer: Guardar requiere al menos una contestación; Enviar exige cero
    // obligatorios pendientes (aunque la evaluación no tenga obligatorios).
    const puedeGuardar = useMemo(() => hayRespuestas(respuestas), [respuestas]);
    const pendientesObligatorios = useMemo(
        () => contarObligatoriosPendientes(reactivosPlanos, respuestas),
        [reactivosPlanos, respuestas],
    );

    // Métricas globales del footer: % de preguntas respondidas (total evaluado)
    // y % de ponderación obtenida respecto al total agregado de todas las
    // secciones. Ambos se recalculan en vivo conforme el usuario contesta.
    // Cuando el servidor devuelve EstatusEvaluacion === COMPLETADA, la
    // evaluación ya fue enviada y se muestra en modo solo-lectura: se
    // deshabilitan todos los inputs y se ocultan Guardar/Enviar.
    const esEvaluacionCompletada = useMemo(
        () => evaluacion?.EstatusEvaluacion === ESTATUS_EVALUACION.COMPLETADA,
        [evaluacion?.EstatusEvaluacion],
    );

    const respondibles = useMemo(
        () => contarRespondiblesRespondidos(reactivosPlanos, respuestas),
        [reactivosPlanos, respuestas],
    );
    // ponderacionGlobal ya está implícita en `ponderacionesPorSeccion`; se
    // mantiene como objeto único para el footer (sintético, no recorre
    // secciones otra vez).
    const ponderacionGlobal = useMemo(() => {
        let total = 0;
        let obtenido = 0;
        ponderacionesPorSeccion.forEach((p) => {
            total += p.total;
            obtenido += p.obtenido;
        });
        return { total: +total.toFixed(4), obtenido: +obtenido.toFixed(4) };
    }, [ponderacionesPorSeccion]);

    const handleSeleccion = (nSeccion: number, nReactivo: number, nOpcion: number) => {
        const key = `${nSeccion}-${nReactivo}`;
        setRespuestas((prev) => ({ ...prev, [key]: { ...prev[key], nOpcion } }));
    };

    const handleObservacion = (nSeccion: number, nReactivo: number, cObservacion: string) => {
        const key = `${nSeccion}-${nReactivo}`;
        setRespuestas((prev) => ({ ...prev, [key]: { ...prev[key], cObservacion } }));
    };

    // Guardar: borrador — POST a /Guarda/Evaluacion con bFinalizado=false. En
    // error solo se notifica: el estado local no se limpia para no perder lo
    // contestado.
    const handleGuardar = async ({ bFinalizado }: { bFinalizado: number }) => {
        if (!evaluacion) return;
        if (esEvaluacionCompletada) return;

        setGuardando(true);
        const obj = construirPayloadGuardado(evaluacion, respuestas);

        const cRespuestas = obj.respuestasString;
        const cTokenEvaluacion = String(cToken ?? '');

        const response = await simpleFetch({
            url: `${API_URL}Guarda/RespuestasEvaluacion`,
            params: { cToken: cTokenEvaluacion, cRespuestas, bFinalizado },
            method: 'POST',
        });
        
        setGuardando(false);

        if (response.success) {
            toast.success('Evaluación guardada.');
            if (bFinalizado) {
                setEnviado(true);
            }
        } else {
            toast.error('Sucedió un error al guardar la evaluación.');
        }
    };

    // Enviar: la validación de obligatorios ya gates el botón; mismo endpoint,
    // con bFinalizado=true. setEnviado solo si la petición es exitosa, para que
    // el usuario pueda reintentar sin perder sus respuestas.
    // const handleEnviar = async () => {
    //     if (!evaluacion) return;

    //     setEnviando(true);
    //     const cTokenEvaluacion = String(cToken ?? '');
    //     const response = await simpleFetch({
    //         url: `${API_URL}Guarda/Evaluacion`,
    //         params: { cToken: cTokenEvaluacion, ...construirPayloadGuardado(evaluacion, respuestas, true) },
    //         method: 'POST',
    //     });
    //     setEnviando(false);

    //     if (response.success) {
    //         setEnviado(true);
    //         toast.success('Evaluación enviada.');
    //     } else {
    //         toast.error('Sucedió un error al enviar la evaluación.');
    //     }
    // };

    if (loading) {
        return (
            <SurveyLayout>
                <Loading />
            </SurveyLayout>
        );
    }

    if (notFound || !evaluacion) {
        return (
            <SurveyLayout>
                <ErrorMessage subtitle="Evaluación no encontrada." />
            </SurveyLayout>
        );
    }

    // Pantalla final tras un envío exitoso: cMensajeFinal de la evaluación (o
    // fallback), sin secciones ni footer.
    if (enviado) {
        return (
            <SurveyLayout>
                <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16 text-center">
                    <h2 className="text-xl font-bold">
                        {evaluacion.cMensajeFinal ?? 'Gracias por completar la evaluación.'}
                    </h2>
                </div>
            </SurveyLayout>
        );
    }

    return (
        <SurveyLayout>
            <div className="flex flex-col w-full py-2">
                <h2 className="text-xl font-bold text-center">{evaluacion.cDescripcion}</h2>

                {evaluacion.TokenExtraInfo && evaluacion.TokenExtraInfo.length > 0 && (
                    <Card className="my-2" shadow="sm">
                        <CardBody className="gap-1 p-4 bg-default-200">
                            {evaluacion.TokenExtraInfo.map((info, i) => (
                                <div
                                    key={i}
                                    className="flex items-baseline justify-between gap-3 border-b py-1 last:border-b-0"
                                >
                                    <span className="font-medium">
                                        {info.cDataDescripcion}
                                    </span>
                                    <span className="text-sm">
                                        {info.cDataValor}
                                    </span>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                )}

                {secciones.map(({ seccion, reactivos }) => {
                    // Lookup O(1) en el Map memoizado, en lugar de recalcular
                    // `calcularPonderacionSeccion` dentro del render.
                    const ponderacion = ponderacionesPorSeccion.get(seccion.nSeccion) ?? { total: 0, obtenido: 0 };
                    const mostrarHeader = mostrarHeaderSeccion(seccion);
                    // Solo se muestra el indicador si la sección tiene reactivos
                    // ponderables (total > 0); sin respuesta el obtenido es 0%.
                    const ponderacionTexto =
                        ponderacion.total > 0
                            ? `${formatPonderacion(ponderacion.obtenido)} de ${formatPonderacion(ponderacion.total)}`
                            : null;

                    return (
                        <section key={seccion.nSeccion} className='bg-default-100 my-2 p-4'>
                            {mostrarHeader && (
                                <div className="flex items-baseline justify-between gap-4 pt-6">
                                    <h4 className="font-bold text-lg">{seccion.cSeccion}</h4>
                                    {ponderacionTexto && (
                                        <span className="text-sm whitespace-nowrap text-default-500">
                                            {ponderacionTexto}
                                        </span>
                                    )}
                                </div>
                            )}
                            {!mostrarHeader && ponderacionTexto && (
                                <p className="pt-6 text-right text-sm text-default-500">
                                    {ponderacionTexto}
                                </p>
                            )}

                            {reactivos.map(({ reactivo, opciones, opcionesByOpcion }) => {
                                const key = `${reactivo.nSeccion}-${reactivo.nReactivo}`;
                                const respuesta = respuestas[key];

                                return (
                                    <ReactivoRow
                                        key={key}
                                        reactivo={reactivo}
                                        opciones={opciones}
                                        opcionesByOpcion={opcionesByOpcion}
                                        seleccion={respuesta?.nOpcion}
                                        observacion={respuesta?.cObservacion}
                                        isDisabled={esEvaluacionCompletada}
                                        onSeleccion={(nOpcion) =>
                                            handleSeleccion(reactivo.nSeccion, reactivo.nReactivo, nOpcion)
                                        }
                                        onObservacion={(texto) =>
                                            handleObservacion(reactivo.nSeccion, reactivo.nReactivo, texto)
                                        }
                                    />
                                );
                            })}
                        </section>
                    );
                })}
            </div>

            {/* Footer sticky: siempre visible mientras se recorre la evaluación.
                El -mx-10/px-10 lo extiende a los bordes de la card de SurveyLayout.
                Se oculta cuando la evaluación ya está COMPLETADA (modo solo-lectura). */}
            
                <div className="
                    sticky bottom-0 z-10  mt-4 flex flex-wrap items-center
                    justify-between gap-3 border-t border-default-200 bg-default-50 px-10 py-4
                ">
                    {
                        !esEvaluacionCompletada && (
                            <Button
                                className="min-w-32"
                                variant="flat"
                                isDisabled={!puedeGuardar}
                                isLoading={guardando}
                                onPress={() => handleGuardar({ bFinalizado: 0 })}
                            >
                                Guardar
                            </Button>
                        )
                    }

                    <div className="flex flex-col gap-1 text-sm text-default-500 sm:flex-col sm:gap-4 max-w-full">
                        {pendientesObligatorios > 0 && (
                            <span>
                                Faltan {pendientesObligatorios} pregunta{pendientesObligatorios === 1 ? '' : 's'} obligatoria{pendientesObligatorios === 1 ? '' : 's'} por responder
                            </span>
                        )}
                        <div className="flex flex-wrap gap-4 justify-center">
                            {/* total evaluado: % de preguntas respondidas vs total respondibles. */}
                            <span className="whitespace-nowrap">
                                Total evaluado: {respondibles.total > 0
                                    ? formatPonderacion(respondibles.respondidos / respondibles.total)
                                    : '—'}
                            </span>
                            {/* Ponderación global agregada: misma métrica que ya muestran los
                                headers de sección ("X% de Y%"), pero a nivel de toda la evaluación. */}
                            {ponderacionGlobal.total > 0 && (
                                <span className="whitespace-nowrap">
                                    Ponderación: {formatPonderacion(ponderacionGlobal.obtenido)} de {formatPonderacion(ponderacionGlobal.total)}
                                </span>
                            )}
                        </div>
                    </div>
                    {
                        !esEvaluacionCompletada && (
                            <Button
                                className="min-w-32"
                                color="primary"
                                isDisabled={pendientesObligatorios > 0}
                                isLoading={guardando}
                                onPress={() => handleGuardar({ bFinalizado: 1 })}
                            >
                                Enviar
                            </Button>
                        )
                    }
                </div>
        </SurveyLayout>
    );
};

export default EvaluacionView;
